<template>
  <v-bottom-sheet
    :model-value="modelValue"
    height="auto"
    :scrim="true"
    class="pwa-install-ios-sheet"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pwa-install-ios-sheet__card" rounded="t-xl">
      <div class="pwa-install-ios-sheet__handle">
        <div class="pwa-install-ios-sheet__handle-bar" />
      </div>

      <div class="pwa-install-ios-sheet__body">
        <div class="pwa-install-ios-sheet__hero">
          <div class="pwa-install-ios-sheet__hero-text">
            <h2 class="pwa-install-ios-sheet__title">
              Add {{ appName }} to your Home Screen
            </h2>
            <p class="pwa-install-ios-sheet__intro">
              You can use {{ appName }} like a regular app. Follow the steps
              below to add it to your Home Screen.
            </p>
          </div>
          <div class="pwa-install-ios-sheet__hero-icon" aria-hidden="true">
            <img
              v-if="useExternalLogo"
              :src="resolvedLogoSrc"
              alt=""
              class="pwa-install-ios-sheet__logo"
            />
            <span
              v-else
              class="pwa-install-ios-sheet__logo-inline"
              v-html="gunclubLogoMarkSvg"
            />
          </div>
        </div>

        <ul class="pwa-install-ios-sheet__steps" role="list">
          <li class="pwa-install-ios-sheet__step">
            <div class="pwa-install-ios-sheet__step-icon-wrap">
              <!-- SF “square.and.arrow.up” / iOS Safari toolbar (not mdi-share-variant nodes) -->
              <v-icon size="22" class="pwa-install-ios-sheet__step-icon"
                >mdi-export</v-icon
              >
            </div>
            <div class="pwa-install-ios-sheet__step-copy">
              <p class="pwa-install-ios-sheet__step-title">
                1. Tap the Share icon
              </p>
              <p class="pwa-install-ios-sheet__step-sub">
                It’s the square with an arrow pointing up — usually at the
                bottom of the screen on iPhone.
              </p>
            </div>
          </li>

          <li class="pwa-install-ios-sheet__step">
            <div class="pwa-install-ios-sheet__step-icon-wrap">
              <v-icon size="22" class="pwa-install-ios-sheet__step-icon"
                >mdi-plus-circle-outline</v-icon
              >
            </div>
            <div class="pwa-install-ios-sheet__step-copy">
              <p class="pwa-install-ios-sheet__step-title">
                2. Scroll and tap “Add to Home Screen”
              </p>
              <p class="pwa-install-ios-sheet__step-sub">
                You may need to scroll down in the share sheet to find it.
              </p>
            </div>
          </li>

          <li class="pwa-install-ios-sheet__step">
            <div class="pwa-install-ios-sheet__step-icon-wrap">
              <v-icon size="22" class="pwa-install-ios-sheet__step-icon"
                >mdi-plus</v-icon
              >
            </div>
            <div class="pwa-install-ios-sheet__step-copy">
              <p class="pwa-install-ios-sheet__step-title">
                3. Tap “Add” in the corner
              </p>
              <p class="pwa-install-ios-sheet__step-sub">
                Confirm, and the app icon appears on your Home Screen.
              </p>
            </div>
          </li>
        </ul>

        <div class="pwa-install-ios-sheet__done">
          <div class="pwa-install-ios-sheet__done-icon">
            <img
              v-if="useExternalLogo"
              :src="resolvedLogoSrc"
              alt=""
              class="pwa-install-ios-sheet__logo"
            />
            <span
              v-else
              class="pwa-install-ios-sheet__logo-inline"
              v-html="gunclubLogoMarkSvg"
            />
          </div>
          <p class="pwa-install-ios-sheet__done-text">
            Done! Open {{ appName }} anytime from your Home Screen.
          </p>
        </div>

        <v-btn
          block
          color="primary"
          size="large"
          rounded="lg"
          class="pwa-install-ios-sheet__cta"
          @click="close"
        >
          Got it
        </v-btn>
      </div>
    </v-card>
  </v-bottom-sheet>
</template>

<script setup lang="ts">
import { computed } from "vue";
import defaultMarkUrl from "@/assets/communities-logo.svg?url";
import gunclubLogoMarkSvg from "@/assets/communities-logo.svg?raw";

const props = defineProps<{
  modelValue: boolean;
  /** Display name from branding (e.g. Gunclub). */
  appName: string;
  /** Optional icon URL from API branding (e.g. 192px); falls back to bundled mark. */
  logoUrl?: string | null;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

/** Tenant / remote icon uses `<img>`; bundled mark uses inline SVG so `currentColor` works. */
const useExternalLogo = computed(() => Boolean(props.logoUrl?.trim()));

const resolvedLogoSrc = computed(() => {
  const u = props.logoUrl?.trim();
  if (u) return u;
  return defaultMarkUrl;
});

function close(): void {
  emit("update:modelValue", false);
}
</script>

<style scoped lang="scss">
.pwa-install-ios-sheet {
  :deep(.v-bottom-sheet__content) {
    max-height: min(92vh, 720px);
  }

  &__card {
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    background: rgb(var(--v-theme-surface));
    border-bottom: none;
  }

  &__handle {
    display: flex;
    justify-content: center;
    padding: 10px 0 4px;
  }

  &__handle-bar {
    width: 36px;
    height: 4px;
    border-radius: 9999px;
    background: rgba(var(--v-theme-on-surface), 0.2);
  }

  &__body {
    padding: 8px 20px 12px;
    max-width: 480px;
    margin: 0 auto;
  }

  &__hero {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 20px;
  }

  &__hero-text {
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin: 0 0 8px;
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.25;
    color: rgb(var(--v-theme-on-surface));
    letter-spacing: -0.02em;
  }

  &__intro {
    margin: 0;
    font-size: 0.9375rem;
    line-height: 1.45;
    color: rgba(var(--v-theme-on-surface), 0.68);
  }

  &__hero-icon {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    background: rgb(255, 255, 255);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    box-sizing: border-box;
    /* Solid black mark (currentColor); avoid theme on-surface gray / parent opacity */
    color: #000000;
    opacity: 1;

    .v-theme--dark & {
      background: rgb(0, 0, 0);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
      color: #ffffff;
    }
  }

  &__logo {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  &__logo-inline {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000000;
    opacity: 1;
    line-height: 0;

    .v-theme--dark & {
      color: #ffffff;
    }

    :deep(svg) {
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
      display: block;
      color: inherit;
      opacity: 1;
    }
  }

  &__steps {
    list-style: none;
    margin: 0 0 18px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__step {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    padding: 14px;
    border-radius: 12px;
    background: rgba(var(--v-theme-on-surface), 0.04);
    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  }

  &__step-icon-wrap {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--v-theme-primary), 0.1);
  }

  &__step-icon {
    color: rgb(var(--v-theme-primary));
    opacity: 0.95;
  }

  &__step-copy {
    min-width: 0;
  }

  &__step-title {
    margin: 0 0 4px;
    font-size: 0.9375rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    line-height: 1.3;
  }

  &__step-sub {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.4;
    color: rgba(var(--v-theme-on-surface), 0.62);
  }

  &__done {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    margin-bottom: 16px;
    border-radius: 12px;
    background: rgba(var(--v-theme-primary), 0.06);
    border: 1px solid rgba(var(--v-theme-primary), 0.15);
  }

  &__done-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    overflow: hidden;
    background: rgb(255, 255, 255);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 7px;
    box-sizing: border-box;
    color: #000000;
    opacity: 1;

    .v-theme--dark & {
      background: rgb(0, 0, 0);
      color: #ffffff;
    }
  }

  &__done-text {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.35;
    color: rgb(var(--v-theme-on-surface));
  }

  &__cta {
    font-weight: 600;
    letter-spacing: 0.02em;
  }
}
</style>
